// Phase 24d · Host checkin scanner (mobile-optimized, manual input for MVP)
import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { QrCode, CheckCircle2, XCircle, ArrowLeft, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function EventCheckin() {
    const { id } = useParams();
    const [token, setToken] = useState("");
    const [busy, setBusy] = useState(false);
    const [report, setReport] = useState(null);
    const [recent, setRecent] = useState([]);
    const [lastResult, setLastResult] = useState(null);

    const loadReport = useCallback(async () => {
        try {
            const r = await fetch(`${API}/api/admin/tickets/events/${id}/checkin-report`, { credentials: "include" });
            if (r.ok) setReport(await r.json());
        } catch {}
    }, [id]);

    useEffect(() => { loadReport(); }, [loadReport]);

    const scan = async (raw) => {
        const t = (raw || token).trim();
        if (!t || busy) return;
        setBusy(true);
        try {
            const isJwt = t.split(".").length === 3;
            const body = isJwt ? { qr_token: t } : { ticket_id: t };
            const r = await fetch(`${API}/api/tickets/checkin`, {
                method: "POST", credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            const d = await r.json().catch(() => ({}));
            if (r.ok) {
                setLastResult(d);
                setRecent((prev) => [d, ...prev].slice(0, 20));
                if (d.result === "ok") toast.success("Checked in!");
                else if (d.result === "duplicate") toast.warning(`Duplicate scan (#${d.scan_count})`);
                else toast.error(d.result || "Invalid");
                loadReport();
                setToken("");
            } else {
                toast.error(d.detail?.error || "Checkin failed");
            }
        } catch { toast.error("Network error"); }
        setBusy(false);
    };

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-[#FFFFFF] font-inter">
            <header className="border-b border-[#1A1A1A] backdrop-blur-md bg-[#0A0A0A]/80 sticky top-0 z-40">
                <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
                    <Link to={`/events/${id}`} className="text-neutral-400 hover:text-neutral-200 flex items-center gap-1 text-sm">
                        <ArrowLeft size={16} /> Event
                    </Link>
                    <span className="text-neutral-600">·</span>
                    <h1 className="text-base font-semibold flex items-center gap-2">
                        <QrCode size={16} className="text-[#FFFFFF]" /> Checkin
                    </h1>
                </div>
            </header>

            <main className="max-w-2xl mx-auto px-4 py-6 space-y-4">
                {report && (
                    <div className="grid grid-cols-3 gap-3" data-testid="checkin-counters">
                        <div className="bg-[#111111] border border-[#1A1A1A] rounded-xl p-3 text-center">
                            <p className="text-xs text-neutral-500">Confirmed</p>
                            <p className="text-2xl font-bold">{report.confirmed}</p>
                        </div>
                        <div className="bg-[#111111] border border-[#CCCCCC]/40 rounded-xl p-3 text-center">
                            <p className="text-xs text-neutral-500">Checked in</p>
                            <p className="text-2xl font-bold text-[#CCCCCC]" data-testid="checkin-counter-checked">{report.checked_in}</p>
                        </div>
                        <div className="bg-[#111111] border border-[#1A1A1A] rounded-xl p-3 text-center">
                            <p className="text-xs text-neutral-500">Pending</p>
                            <p className="text-2xl font-bold text-neutral-400">{report.pending}</p>
                        </div>
                    </div>
                )}

                <div className="bg-[#111111] border border-[#1A1A1A] rounded-2xl p-5" data-testid="checkin-scanner">
                    <p className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Scan or paste ticket / QR token</p>
                    <div className="flex items-stretch gap-2">
                        <input
                            data-testid="checkin-token-input"
                            value={token} onChange={(e) => setToken(e.target.value)}
                            placeholder="Paste JWT or ticket_id…"
                            className="flex-1 bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg px-3 py-2 text-sm font-mono"
                        />
                        <button
                            onClick={() => scan()}
                            disabled={busy || !token.trim()}
                            data-testid="checkin-scan-btn"
                            className="px-4 py-2 rounded-lg bg-white text-black text-sm font-semibold disabled:opacity-40"
                        >
                            {busy ? "…" : "Check in"}
                        </button>
                    </div>
                    <button
                        onClick={loadReport}
                        className="text-[10px] text-[#CCCCCC] hover:underline inline-flex items-center gap-1 mt-2"
                    >
                        <RefreshCw size={10} /> Refresh counter
                    </button>
                </div>

                {lastResult && (
                    <div className={`bg-[#111111] border rounded-xl p-4 flex items-center gap-3 ${
                        lastResult.result === "ok" ? "border-[#CCCCCC]/40" :
                        lastResult.result === "duplicate" ? "border-neutral-500/40" : "border-[#FAFAFA]/40"
                    }`} data-testid="checkin-last-result">
                        {lastResult.result === "ok" ? <CheckCircle2 className="text-[#CCCCCC]" /> :
                         lastResult.result === "duplicate" ? <RefreshCw className="text-neutral-400" /> :
                         <XCircle className="text-[#FAFAFA]" />}
                        <div className="text-sm flex-1">
                            <p className="font-semibold capitalize">{lastResult.result}</p>
                            <p className="text-[10px] text-neutral-500 font-mono">{lastResult.ticket_id}</p>
                        </div>
                    </div>
                )}

                <div>
                    <p className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Recent scans</p>
                    <div className="space-y-1" data-testid="checkin-recent-list">
                        {recent.length === 0 ? (
                            <p className="text-sm text-neutral-500">No scans yet.</p>
                        ) : recent.map((r, i) => (
                            <div key={i} className="text-xs flex items-center justify-between bg-[#111111] border border-[#1A1A1A] rounded-lg px-3 py-1.5">
                                <span className="font-mono text-neutral-400">{r.ticket_id?.slice(0, 12)}…</span>
                                <span className={`text-[10px] uppercase font-bold ${
                                    r.result === "ok" ? "text-[#CCCCCC]" :
                                    r.result === "duplicate" ? "text-neutral-400" : "text-[#FAFAFA]"}`}>
                                    {r.result}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </main>
        </div>
    );
}
